/**
 * components/accounting/ExpenseTable.tsx
 * Expenses table with inline edit and delete actions.
 * - Filters by category and payment method and shows filtered totals.
 * - Locked when selected month is closed.
 * - Entry users: no edit/delete and no totals bar.
 * - Viewer: sees totals bar (full details) but cannot edit/delete.
 */

import { use, useMemo, useState } from 'react'
import { useAccountingStore } from '../../store/accounting'
import { formatCurrency, formatDate } from '../../lib/format'
import { CheckCircle2, Edit3, Trash2, CircleDot } from 'lucide-react'
import { isAfter, startOfDay } from 'date-fns'
import type { Expense } from '../../types/entites'
import { useSelectedMonth } from '../../hooks/useMonth'
import { useAccountingStore as useAcc } from '../../store/accounting'
import { useAuthStore } from '../../store/auth'
import { useUserStore } from '@/store/user'

interface RowEdit {
  id: string
  description: string
  category: string
  amount: string
  paymentDate: string
  paymentMethod: string
}

interface ExpenseTableProps {
  monthRange?: { start: Date; end: Date } | null
}

function withinMonth(e: Expense, range?: { start: Date; end: Date } | null) {
  if (!range) return true
  if (e.paid && e.paid_at) {
    const d = new Date(e.paid_at)
    return d >= range.start && d <= range.end
  }
  const d = new Date(e.payment_date)
  return d >= range.start && d <= range.end
}

export function ExpenseTable({ monthRange = null }: ExpenseTableProps) {
  const expenses = useUserStore((s) => s.expenses)
  //const expenses = useAccountingStore((s) => s.expenses)
  const updateExpense = useUserStore((s) => s.updateExpense)
  const removeExpense = useUserStore((s) => s.removeExpense)
  // const updateExpense = useAccountingStore((s) => s.updateExpense)
  // const removeExpense = useAccountingStore((s) => s.removeExpense)

  const [editing, setEditing] = useState<RowEdit | null>(null)

  const [categoryFilter, setCategoryFilter] = useState<string>('الكل')
  const [methodFilter, setMethodFilter] = useState<string>('الكل')

  const { month } = useSelectedMonth()
  const getClosure = useAcc((s) => s.getClosure)
  const isClosed = Boolean(getClosure(month))
  // const currentUser = useAuthStore((s) => s.getCurrentUser())
  // const role = currentUser?.role || 'entry'

  const role = useUserStore((s) => s.role)
  const isAdmin = role === 'admin'
  const isViewer = role === 'viewer'
  const noEditMode = isClosed || !isAdmin

  const categories = useMemo(() => {
    const set = new Set<string>()
    for (const e of expenses) {
      if (e.category) set.add(e.category)
    }
    return ['الكل', ...Array.from(set)]
  }, [expenses])

  const methods = useMemo(() => {
    const set = new Set<string>()
    for (const e of expenses) {
      if (e.payment_method) set.add(e.payment_method)
    }
    return ['الكل', ...Array.from(set)]
  }, [expenses])

  const filtered: Expense[] = useMemo(() => {
    return expenses.filter((e) => {
      const passCategory = categoryFilter === 'الكل' || e.category === categoryFilter
      const passMethod = methodFilter === 'الكل' || e.payment_method === methodFilter
      const passMonth = withinMonth(e, monthRange || undefined)
      return passCategory && passMethod && passMonth
    })
  }, [expenses, categoryFilter, methodFilter, monthRange])

  const filteredTotals = useMemo(() => {
    const totalAmount = filtered.reduce((s, e) => s + e.amount, 0)
    return { count: filtered.length, totalAmount }
  }, [filtered])

  const ordered: Expense[] = useMemo(() => {
    return [...filtered].sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
  }, [filtered])

  const onEdit = (id: string) => {
    if (noEditMode) return
    const e = expenses.find((x) => x.id === id)
    if (!e) return
    setEditing({
      id,
      description: e.description,
      category: e.category,
      amount: String(e.amount),
      paymentDate: e.payment_date.slice(0, 10),
      paymentMethod: e.payment_method || '',
    })
  }

  const onSave = () => {
    if (!editing || noEditMode) return
    const amt = Number(editing.amount)
    if (isNaN(amt) || amt <= 0) return
    updateExpense(editing.id, {
      description: editing.description,
      category: editing.category,
      amount: amt,
      payment_date: editing.paymentDate,
      payment_method: editing.paymentMethod || undefined,
    })
    setEditing(null)
  }

  const onCancel = () => setEditing(null)

  const onDelete = (id: string) => {
    if (noEditMode) return
    const ok = confirm('تأكيد حذف المصروف؟ لا يمكن التراجع.')
    if (ok) removeExpense(id)
  }

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-slate-600">التصنيف</label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          >
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-600">طريقة الدفع</label>
          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          >
            {methods.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filtered totals: show for admin and viewer */}
      {(isAdmin || isViewer) && (
        <div
          className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          title="إجمالي نتائج الفلترة الحالية"
        >
          <span className="text-slate-700">إجمالي المبالغ لنتيجة الفلترة</span>
          <span className="font-semibold text-slate-900">
            {formatCurrency(filteredTotals.totalAmount)} • عدد: {filteredTotals.count}
          </span>
        </div>
      )}

      {isClosed && (
        <div className="rounded-lg bg-amber-50 p-2 text-sm text-amber-800">الشهر مُقفَل. التعديل/الحذف معطّل.</div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-right font-medium text-gray-700">التاريخ</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">الوصف</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">التصنيف</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">طريقة الدفع</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">المبلغ</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">تاريخ الدفع</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">الحالة</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">إجراءات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {ordered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  لا توجد مصروفات حالياً.
                </td>
              </tr>
            ) : (
              ordered.map((e) => {
                const isPending = !e.paid && isAfter(startOfDay(new Date(e.payment_date)), startOfDay(new Date()))
                const status = isPending ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-amber-800">
                    <CircleDot className="h-4 w-4" /> مؤجلة
                  </span>
                ) : e.paid ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-1 text-rose-800">
                    <CheckCircle2 className="h-4 w-4" /> مدفوع
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-1 text-rose-800">
                    <CheckCircle2 className="h-4 w-4" /> مدفوع
                  </span>
                )

                const isRowEditing = editing?.id === e.id

                return (
                  <tr key={e.id}>
                    <td className="px-3 py-2 text-gray-700">{formatDate(e.created_at)}</td>

                    <td className="px-3 py-2">
                      {isRowEditing ? (
                        <input
                          value={editing.description}
                          onChange={(e2) => setEditing((s) => (s ? { ...s, description: e2.target.value } : s))}
                          className="w-full rounded border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          disabled={noEditMode}
                        />
                      ) : (
                        <span className="text-gray-800">{e.description || '-'}</span>
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {isRowEditing ? (
                        <input
                          value={editing.category}
                          onChange={(e2) => setEditing((s) => (s ? { ...s, category: e2.target.value } : s))}
                          className="w-full rounded border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          disabled={noEditMode}
                        />
                      ) : (
                        <span className="text-gray-800">{e.category}</span>
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {isRowEditing ? (
                        <input
                          value={editing.paymentMethod}
                          onChange={(e2) => setEditing((s) => (s ? { ...s, paymentMethod: e2.target.value } : s))}
                          className="w-full rounded border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          placeholder="تحويل بنكي، نقدًا، ..."
                          list="expense-payment-methods"
                          disabled={noEditMode}
                        />
                      ) : (
                        <span className="text-gray-800">{e.payment_method || '-'}</span>
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {isRowEditing ? (
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          value={editing.amount}
                          onChange={(e2) => setEditing((s) => (s ? { ...s, amount: e2.target.value } : s))}
                          className="w-28 rounded border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          disabled={noEditMode}
                        />
                      ) : (
                        <span className="text-gray-800">{formatCurrency(e.amount)}</span>
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {isRowEditing ? (
                        <input
                          type="date"
                          value={editing.paymentDate}
                          onChange={(e2) => setEditing((s) => (s ? { ...s, paymentDate: e2.target.value } : s))}
                          className="w-40 rounded border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          disabled={noEditMode}
                        />
                      ) : (
                        <span className="text-gray-800">{formatDate(e.payment_date)}</span>
                      )}
                    </td>

                    <td className="px-3 py-2">{status}</td>

                    <td className="px-3 py-2">
                      {isRowEditing ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={onSave}
                            disabled={noEditMode}
                            className="rounded bg-emerald-600 px-3 py-1 text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            حفظ
                          </button>
                          <button
                            onClick={onCancel}
                            className="rounded bg-gray-200 px-3 py-1 text-gray-800 hover:bg-gray-300"
                          >
                            إلغاء
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onEdit(e.id)}
                            disabled={noEditMode}
                            className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-1 text-white hover:bg-indigo-700 disabled:opacity-50"
                          >
                            <Edit3 className="h-4 w-4" /> تعديل
                          </button>
                          <button
                            onClick={() => onDelete(e.id)}
                            disabled={noEditMode}
                            className="inline-flex items-center gap-1 rounded bg-rose-600 px-3 py-1 text-white hover:bg-rose-700 disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" /> حذف
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
